// src/components/settings/ProjectForm.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  AlertCircle, 
  CheckCircle, 
  FolderPlus,
  MapPin,
  Building,
  Users,
  FileText,
  Upload,
  Image,
  Trash2,
  School
} from 'lucide-react';
import { ProjectService } from '../../services/projectService';
import { 
  getUniqueProvinces, 
  getDistrictsByProvince, 
  getSchoolsByFilters 
} from '../../datalists/Schools';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../services/firebase';

const ProjectForm = ({ project, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    province: '',
    district: '',
    circuit: '',
    projectLevel: 'District', // New field
    status: 'active',
    startDate: '',
    endDate: '',
    objectives: [''],
    budget: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    memberSchools: [], // New field - array of school objects
    logoUrl: '', // New field
    logoFile: null // For upload
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Schools integration state
  const [provinces] = useState(getUniqueProvinces());
  const [districts, setDistricts] = useState([]);
  const [availableSchools, setAvailableSchools] = useState([]);
  const [selectedSchoolsToAdd, setSelectedSchoolsToAdd] = useState([]);
  const [schoolSearchTerm, setSchoolSearchTerm] = useState('');

  // Project levels
  const projectLevels = ['Provincial', 'District', 'Circuit', 'School'];

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
        province: project.province || '',
        district: project.district || '',
        circuit: project.circuit || '',
        projectLevel: project.projectLevel || 'District',
        status: project.status || 'active',
        startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
        endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
        objectives: project.objectives || [''],
        budget: project.budget || '',
        contactPerson: project.contactPerson || '',
        contactEmail: project.contactEmail || '',
        contactPhone: project.contactPhone || '',
        memberSchools: project.memberSchools || [],
        logoUrl: project.logoUrl || '',
        logoFile: null
      });
    }
  }, [project]);

  // Update districts when province changes
  useEffect(() => {
    if (formData.province) {
      const provinceDistricts = getDistrictsByProvince(formData.province);
      setDistricts(provinceDistricts);
      
      // Reset district if it's not in the new province
      if (formData.district && !provinceDistricts.includes(formData.district)) {
        setFormData(prev => ({ ...prev, district: '', circuit: '' }));
      }
    } else {
      setDistricts([]);
    }
  }, [formData.province]);

  // Load available schools when location changes
  useEffect(() => {
    if (formData.province && formData.district) {
      const filters = {
        province: formData.province,
        district: formData.district
      };
      
      if (formData.circuit) {
        filters.circuit = formData.circuit;
      }

      const schools = getSchoolsByFilters(filters);
      setAvailableSchools(schools);
    } else {
      setAvailableSchools([]);
    }
  }, [formData.province, formData.district, formData.circuit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleObjectiveChange = (index, value) => {
    const newObjectives = [...formData.objectives];
    newObjectives[index] = value;
    setFormData(prev => ({
      ...prev,
      objectives: newObjectives
    }));
  };

  const addObjective = () => {
    setFormData(prev => ({
      ...prev,
      objectives: [...prev.objectives, '']
    }));
  };

  const removeObjective = (index) => {
    if (formData.objectives.length > 1) {
      const newObjectives = formData.objectives.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        objectives: newObjectives
      }));
    }
  };

  // Logo upload handling
  const handleLogoUpload = async (file) => {
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('Logo file size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Logo must be an image file');
      return;
    }

    try {
      setUploadingLogo(true);
      
      // Delete existing logo if updating
      if (formData.logoUrl && project) {
        try {
          const oldLogoRef = ref(storage, `project-logos/${project.id}/logo`);
          await deleteObject(oldLogoRef);
        } catch (err) {
          console.warn('Could not delete old logo:', err);
        }
      }

      // Upload new logo
      const logoRef = ref(storage, `project-logos/${project?.id || 'temp'}/logo-${Date.now()}`);
      const snapshot = await uploadBytes(logoRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      setFormData(prev => ({
        ...prev,
        logoUrl: downloadURL,
        logoFile: file
      }));
      
      setError('');
    } catch (err) {
      setError('Failed to upload logo');
      console.error('Logo upload error:', err);
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    if (formData.logoUrl && project) {
      try {
        const logoRef = ref(storage, `project-logos/${project.id}/logo`);
        await deleteObject(logoRef);
      } catch (err) {
        console.warn('Could not delete logo:', err);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      logoUrl: '',
      logoFile: null
    }));
  };

  // Member schools handling
  const addMemberSchool = (school) => {
    if (!formData.memberSchools.find(ms => ms.schoolName === school.schoolName)) {
      setFormData(prev => ({
        ...prev,
        memberSchools: [...prev.memberSchools, {
          schoolName: school.schoolName,
          province: school.province,
          district: school.district,
          circuit: school.circuit || '',
          phase: school.phase || '',
          addedAt: new Date()
        }]
      }));
    }
    setSelectedSchoolsToAdd([]);
    setSchoolSearchTerm('');
  };

  const removeMemberSchool = (index) => {
    setFormData(prev => ({
      ...prev,
      memberSchools: prev.memberSchools.filter((_, i) => i !== index)
    }));
  };

  const filteredAvailableSchools = availableSchools.filter(school => 
    school.schoolName.toLowerCase().includes(schoolSearchTerm.toLowerCase()) &&
    !formData.memberSchools.find(ms => ms.schoolName === school.schoolName)
  );

  const validateForm = () => {
    const required = ['name', 'province', 'district', 'contactPerson', 'projectLevel'];
    
    for (const field of required) {
      if (!formData[field].trim()) {
        return `${field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')} is required`;
      }
    }

    if (formData.contactEmail && !/\S+@\S+\.\S+/.test(formData.contactEmail)) {
      return 'Please enter a valid email address';
    }

    if (formData.startDate && formData.endDate) {
      if (new Date(formData.startDate) >= new Date(formData.endDate)) {
        return 'End date must be after start date';
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);

      const projectData = {
        ...formData,
        objectives: formData.objectives.filter(obj => obj.trim()),
        startDate: formData.startDate ? new Date(formData.startDate) : null,
        endDate: formData.endDate ? new Date(formData.endDate) : null,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        memberSchools: formData.memberSchools
      };

      // Remove logoFile from data sent to Firestore
      delete projectData.logoFile;

      if (project) {
        await ProjectService.updateProject(project.id, projectData);
        setSuccess('Project updated successfully!');
      } else {
        await ProjectService.createProject(projectData);
        setSuccess('Project created successfully!');
      }

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <FolderPlus className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-medium text-gray-900">
                {project ? 'Edit Project' : 'Create New Project'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-6 space-y-8">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            )}

            {success && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <p className="text-green-700 text-sm">{success}</p>
                </div>
              </div>
            )}

            {/* Project Logo Section */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 flex items-center gap-2">
                <Image className="w-4 h-4" />
                Project Logo
              </h4>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1">
                  {formData.logoUrl ? (
                    <div className="relative">
                      <img
                        src={formData.logoUrl}
                        alt="Project Logo"
                        className="w-full h-32 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <Image className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No logo uploaded</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Logo
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleLogoUpload(file);
                    }}
                    disabled={uploadingLogo}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum file size: 5MB. Recommended: 400x200px
                  </p>
                  {uploadingLogo && (
                    <p className="text-sm text-blue-600 mt-2">Uploading logo...</p>
                  )}
                </div>
              </div>
            </div>

            {/* Basic Information - Spreadsheet Style Layout */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Basic Information
              </h4>
              
              {/* Row 1: Project Name, Level, and Status */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter project name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Level *
                  </label>
                  <select
                    name="projectLevel"
                    value={formData.projectLevel}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {projectLevels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe the project goals and scope"
                />
              </div>
            </div>

            {/* Location Information - Spreadsheet Style Layout */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Location Details
              </h4>
              
              {/* Row 3: Location fields */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Province *
                  </label>
                  <select
                    name="province"
                    value={formData.province}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Province</option>
                    {provinces.map(province => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    District *
                  </label>
                  <select
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!formData.province}
                  >
                    <option value="">Select District</option>
                    {districts.map(district => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Circuit (Optional)
                  </label>
                  <input
                    type="text"
                    name="circuit"
                    value={formData.circuit}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter circuit"
                  />
                </div>
              </div>
            </div>

            {/* Member Schools Section */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 flex items-center gap-2">
                <School className="w-4 h-4" />
                Member Schools ({formData.memberSchools.length})
              </h4>

              {/* Add Schools */}
              {availableSchools.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add Schools from {formData.district}, {formData.province}
                  </label>
                  <div className="relative mb-3">
                    <input
                      type="text"
                      placeholder="Search schools..."
                      value={schoolSearchTerm}
                      onChange={(e) => setSchoolSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  {filteredAvailableSchools.length > 0 && (
                    <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md">
                      {filteredAvailableSchools.slice(0, 10).map((school, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => addMemberSchool(school)}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="text-sm font-medium">{school.schoolName}</div>
                          <div className="text-xs text-gray-500">
                            {school.circuit ? `${school.circuit}, ` : ''}{school.phase}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Current Member Schools */}
              {formData.memberSchools.length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-gray-700">Current Member Schools:</h5>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    {formData.memberSchools.map((school, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-md border border-blue-200">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{school.schoolName}</div>
                          <div className="text-xs text-gray-600">
                            {school.circuit ? `${school.circuit}, ` : ''}{school.district}
                            {school.phase && ` • ${school.phase}`}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeMemberSchool(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Timeline & Budget - Spreadsheet Style Layout */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900">Timeline & Budget</h4>
              
              {/* Row 4: Timeline and Budget */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Budget (Optional)
                  </label>
                  <input
                    type="number"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information - Spreadsheet Style Layout */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Contact Information
              </h4>
              
              {/* Row 5: Contact fields */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Person *
                  </label>
                  <input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+27 XX XXX XXXX"
                  />
                </div>
              </div>
            </div>

            {/* Project Objectives */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-md font-medium text-gray-900">Project Objectives</h4>
                <button
                  type="button"
                  onClick={addObjective}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  + Add Objective
                </button>
              </div>
              
              <div className="space-y-3">
                {formData.objectives.map((objective, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={objective}
                      onChange={(e) => handleObjectiveChange(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={`Objective ${index + 1}`}
                    />
                    {formData.objectives.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeObjective(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingLogo}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {project ? 'Update Project' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectForm;